/*!
 * Basic Jane — Rest Finder
 * Guided sleep & relaxation shopping + education flow for Shopify (Enterprise theme, OS 2.0).
 * Shares relief-finder.css with the Relief Finder.
 *
 * HOW IT WORKS
 *  - Live product data (title, price, image, variant id) comes from the
 *    <script data-rf-products> JSON the Liquid section prints.
 *  - Copy, ingredients and scoring live in the CONFIG below so marketing can
 *    edit them in one place.
 *  - Ingredient definitions are read from the server-rendered glossary
 *    (snippets/rest-finder-learn.liquid) so the SEO copy has a single source.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * 1. CONFIG — edit copy, links and scoring here
   * ------------------------------------------------------------------ */

  var LINKS = {
    sleep: '/blogs/press/hops-and-valerian-the-perfect-combination-for-sleep',
    cbg: '/blogs/press/cbg',
    howToUse: '/pages/how-to-you-use-cbd',
    faq: '/pages/faq',
    coa: '/blogs/basic-training',
    reliefFinder: '/pages/product-finder',
    completeBundle: '/products/complete-relaxation',
    restBundle: '/products/relaxation-rest-bundle',
    relaxLine: '/collections/relax-product-line',
    bundle3: '/collections/buy-any-3-products-and-save-15',
    subscribe: '/collections/subscribe-and-save',
    samples: '/collections/sample-pack'
  };

  var FORMATS = {
    oral: {
      name: 'Take it', article: 'an', long: 'oral option',
      onset: '30–60 min', duration: 'Up to 8 hrs', reach: 'Whole body',
      blurb: 'Capsules, mints or chews. Take 30–60 minutes before you want to feel it.'
    },
    topical: {
      name: 'Aromatherapy & body', article: 'a', long: 'topical',
      onset: 'Minutes', duration: 'Up to 4 hrs', reach: 'Where you apply it + scent',
      blurb: 'Rollers, sprays and creams that pair calming scent with relief right where you apply them.'
    },
    bath: {
      name: 'Bath soak', article: 'a', long: 'bath soak',
      onset: 'During your bath', duration: 'Your wind-down', reach: 'Whole body, warm water',
      blurb: 'A warm (not hot) bath with CBD and essential oils, about an hour before bed.'
    },
    transdermal: {
      name: 'Overnight patch', article: 'a', long: 'transdermal patch',
      onset: '30–60 min', duration: 'Up to 12 hrs', reach: 'Targeted or whole-body',
      blurb: 'Steady, hands-free support that lasts through the night — no waking up to reapply.'
    }
  };

  // Keys must match the keys printed by sections/rest-finder.liquid
  // cbd: false marks products for shoppers who'd rather skip CBD.
  var PRODUCTS = {
    night_caps: {
      phrase: 'Night Caps, our non-habit-forming botanical sleep aid',
      name: 'Night Caps', format: 'oral', type: 'Softgels', cbd: false,
      tagline: 'Valerian root, hops and passion fruit — a non-habit-forming way to drift off. No CBD.',
      detail: '30 softgels · take 1–2 before bed',
      ingredients: ['valerian', 'hops', 'passion_fruit'],
      onset: '30–60 min', duration: 'Up to 8 hrs'
    },
    mints: {
      phrase: 'CBD Mints, a gentle 5 mg dose you can fit into your evening',
      name: 'CBD Mints', format: 'oral', type: 'Mints', cbd: true,
      tagline: 'A simple, consistent 5 mg dose. Start with one and see how you feel.',
      detail: '5 mg CBD per mint · 60 per pouch',
      ingredients: ['cbd', 'peppermint'],
      onset: '30–90 min', duration: 'Up to 6–8 hrs'
    },
    refresh_chews: {
      phrase: 'Refresh Chews, a CBD + CBG daily reset',
      name: 'Refresh Chews', format: 'oral', type: 'Chews', cbd: true,
      onlyIf: function (a) { return a.area === 'stress' || a.area === 'unwind'; },
      tagline: 'CBD + CBG with terpenes — a daytime reset so the stress doesn’t follow you to bed.',
      detail: '20 mg CBD + 5 mg CBG per chew',
      ingredients: ['cbd', 'cbg'],
      onset: 'Up to 1 hr', duration: 'Up to 8 hrs'
    },
    roller_relax: {
      phrase: 'a calming lavender roller for your wrists and temples',
      name: 'Relax Aromatherapy Oil', format: 'topical', type: 'Roller', cbd: true,
      tagline: 'Lavender, ylang-ylang and clary sage with CBD. Roll on wrists and temples and breathe.',
      detail: 'Pocket-size roll-on',
      ingredients: ['cbd', 'lavender', 'ylang_ylang', 'clary_sage'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    spray_relax: {
      phrase: 'Relax CBD Spray, cooling relief with calming lavender',
      name: 'Relax CBD Spray', format: 'topical', type: 'Spray', cbd: true,
      tagline: 'Cooling menthol with lavender and clary sage for tense shoulders at the end of the day.',
      detail: 'No-mess spray',
      ingredients: ['cbd', 'menthol', 'lavender', 'clary_sage'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    bath: {
      phrase: 'a warm CBD bath soak about an hour before bed',
      name: 'CBD Bath Soak', format: 'bath', type: 'Bath salts', cbd: true,
      tagline: 'Warm water, essential oils and 300 mg CBD — one tin makes up to three baths.',
      detail: '300 mg CBD per 6 oz tin',
      ingredients: ['cbd', 'lavender', 'lime'],
      onset: 'During your bath', duration: 'Your wind-down'
    },
    cream: {
      phrase: 'Relieve Cream massaged into sore spots before bed',
      name: 'Relieve Cream', format: 'topical', type: 'Cream', cbd: true,
      tagline: 'Rich, cooling cream for sore muscles — a favorite bedtime ritual.',
      detail: '250 mg CBD per 2 oz',
      ingredients: ['cbd', 'menthol', 'peppermint', 'aloe'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    patch: {
      phrase: 'an overnight CBD patch, so discomfort doesn’t wake you',
      name: 'CBD Patch', format: 'transdermal', type: 'Patch', cbd: true,
      tagline: 'Apply before bed for up to 12 hours of steady, hands-free support.',
      detail: '20 mg CBD per patch · 6 per pouch',
      ingredients: ['cbd', 'aloe', 'coq10', 'black_pepper'],
      onset: '30–60 min', duration: 'Up to 12 hrs'
    },
    sample_oral: { name: 'Oral Sample Pack', format: 'oral', type: 'Sampler', sample: true, cbd: true, ingredients: [] }
  };

  var PRIORITY = ['night_caps', 'roller_relax', 'mints', 'bath', 'spray_relax', 'patch', 'cream', 'refresh_chews'];

  var INGREDIENT_NAMES = {
    valerian: 'Valerian root', hops: 'Hops', passion_fruit: 'Passion fruit', cbd: 'CBD (isolate)', cbg: 'CBG',
    peppermint: 'Peppermint', lavender: 'Lavender', ylang_ylang: 'Ylang-ylang', clary_sage: 'Clary sage',
    menthol: 'Menthol', lime: 'Lime', aloe: 'Aloe', coq10: 'CoQ10', black_pepper: 'Black pepper extract'
  };

  var QUESTIONS = [
    {
      id: 'area',
      title: 'What gets in the way of a good night?',
      help: 'Pick the one that sounds most like you.',
      options: [
        { v: 'fallasleep', label: 'My mind won’t switch off', icon: 'mind', summary: 'trouble switching off at bedtime' },
        { v: 'staysleep', label: 'I wake up during the night', icon: 'wake', summary: 'waking up during the night' },
        { v: 'discomfort', label: 'Aches keep me up', icon: 'ache', summary: 'aches that keep you up' },
        { v: 'stress', label: 'Stress follows me home', icon: 'stress', summary: 'stress that follows you to bed' },
        { v: 'unwind', label: 'I want a better wind-down ritual', icon: 'bath', summary: 'a better evening wind-down' }
      ]
    },
    {
      id: 'pattern',
      title: 'How often does this happen?',
      options: [
        { v: 'occasional', label: 'Now and then', summary: 'now and then' },
        { v: 'several', label: 'A few nights a week', summary: 'a few nights a week' },
        { v: 'most', label: 'Most nights', summary: 'most nights' }
      ]
    },
    {
      id: 'routine',
      title: 'What would you actually stick with?',
      help: 'The best routine is one you’ll keep doing.',
      options: [
        { v: 'take', label: 'Something simple to take' },
        { v: 'aroma', label: 'Calming scents on my wrists and temples' },
        { v: 'bath', label: 'A warm bath before bed' },
        { v: 'body', label: 'Something for sore muscles' }
      ]
    },
    {
      id: 'exp',
      title: 'How do you feel about CBD?',
      options: [
        { v: 'open', label: 'Happy to include it' },
        { v: 'new', label: 'New to it, but curious' },
        { v: 'nocbd', label: 'I’d rather skip CBD' }
      ]
    }
  ];

  var SCORES = {
    area: {
      fallasleep: { night_caps: 5, roller_relax: 2, spray_relax: 1, bath: 1, mints: 1 },
      staysleep: { night_caps: 4, patch: 2, mints: 2 },
      discomfort: { patch: 4, cream: 3, spray_relax: 3, night_caps: 2, bath: 1 },
      stress: { refresh_chews: 6, roller_relax: 3, mints: 2, bath: 2, spray_relax: 1 },
      unwind: { bath: 4, roller_relax: 3, spray_relax: 2, night_caps: 1, refresh_chews: 1 }
    },
    pattern: {
      occasional: { roller_relax: 1, bath: 1, night_caps: 1 },
      several: { night_caps: 2, mints: 1 },
      most: { night_caps: 2, mints: 2, patch: 1 }
    },
    routine: {
      take: { night_caps: 3, mints: 3, refresh_chews: 2 },
      aroma: { roller_relax: 4, spray_relax: 3 },
      bath: { bath: 5 },
      body: { cream: 4, patch: 3, spray_relax: 2 }
    },
    exp: {
      open: { mints: 1, patch: 1 },
      new: { mints: 1 },
      nocbd: { night_caps: 3 }
    }
  };

  var REASONS = {
    area: {
      fallasleep: 'For a busy mind, a calming, consistent pre-bed step makes it easier to switch off.',
      staysleep: 'Longer-lasting support works through the night rather than wearing off by 2 a.m.',
      discomfort: 'When aches are what wake you, addressing the discomfort helps you stay asleep.',
      stress: 'Taking the edge off earlier in the evening makes it easier to switch off later.',
      unwind: 'A consistent ritual signals to your body that it’s time to slow down.'
    },
    pattern: {
      occasional: 'For occasional restless nights, keep something on hand for when you need it.',
      several: 'A few nights a week is a good reason to make this part of your routine.',
      most: 'When it’s most nights, consistency matters — the same routine at the same time each evening.'
    },
    routine: {
      take: 'It’s as simple as it gets — no ritual required.',
      aroma: 'Calming scent is a quick, portable way to start winding down.',
      bath: 'A warm bath about an hour before bed helps you relax as your body cools afterward.',
      body: 'It goes right where you feel it.'
    },
    exp: {
      open: '',
      new: 'A gentle, low-dose place to start if you’re new to CBD.',
      nocbd: 'It contains no CBD — just traditional botanicals.'
    }
  };

  var AREA_TIPS = {
    fallasleep: 'Tip: take Night Caps 30–60 minutes before bed, then dim the lights and put screens away. Start with one softgel and see how you feel the next morning.',
    staysleep: 'Tip: if you wake up at night, avoid checking the clock or your phone. Keep the room cool, dark and quiet.',
    discomfort: 'Tip: apply a patch directly on a sore spot (lower back, hip, shoulder) when you get into bed for up to 12 hours of steady support.',
    stress: 'Tip: try a chew in the late afternoon, then a few slow breaths with a Relax roller on your wrists as you start your evening.',
    unwind: 'Tip: keep your wind-down the same every night — same time, same steps. A warm (not hot) bath about an hour before bed is a great anchor.'
  };

  var AREA_ARTICLES = {
    fallasleep: [{ t: 'Hops and valerian: the perfect combination for sleep', u: LINKS.sleep }],
    staysleep: [{ t: 'Hops and valerian: the perfect combination for sleep', u: LINKS.sleep }],
    discomfort: [{ t: 'Find the right format for aches and pains', u: LINKS.reliefFinder }],
    stress: [{ t: 'What is CBG? How it differs from CBD', u: LINKS.cbg }],
    unwind: [{ t: 'Shop the full Relax line', u: LINKS.relaxLine }]
  };

  var FORMAT_ARTICLES = {
    oral: [{ t: 'How to use oral products — start low, go slow', u: LINKS.howToUse }],
    topical: [{ t: 'How to use CBD topicals and rollers', u: LINKS.howToUse }],
    bath: [{ t: 'How to use CBD products', u: LINKS.howToUse }],
    transdermal: [{ t: 'How to use the CBD patch', u: LINKS.howToUse }]
  };

  var PAIR_TEXT = {
    'oral>topical': 'While {primary} works from the inside, the {pair} turns winding down into a few calming minutes.',
    'oral>bath': 'Start with a warm bath with the {pair}, then take {primary} as you head to bed.',
    'oral>transdermal': 'If aches wake you, add a patch at bedtime alongside {primary}.',
    'topical>oral': 'Use the {primary} as you wind down, and add {pair} on nights you need more support.',
    'topical>bath': 'Make it a ritual: a bath with the {pair}, then the {primary} as you get into bed.',
    'topical>transdermal': 'Use the {primary} as you wind down, and a patch to carry you through the night.',
    'bath>oral': 'Follow your bath with {pair} for support that lasts through the night.',
    'bath>topical': 'After your bath, finish with the {pair}.',
    'transdermal>oral': 'Pair the overnight patch with {pair} for a full bedtime routine.',
    'transdermal>topical': 'Apply the patch as you get into bed, and use the {pair} to unwind beforehand.'
  };

  // Sleep habits shown with every result (education + SEO).
  var HABITS = [
    'Keep the same bedtime and wake time, even on weekends.',
    'Dim lights and put screens away 30–60 minutes before bed.',
    'Keep your bedroom cool, dark and quiet.',
    'Limit caffeine after midday and heavy meals close to bedtime.',
    'If you can’t sleep after about 20 minutes, get up and do something calm until you feel sleepy.'
  ];

  var BUNDLES = [
    { t: 'Complete Relaxation Bundle', d: 'calming CBD favorites plus Night Caps', u: LINKS.completeBundle },
    { t: 'Rest/Relaxation Bundle', d: 'topical, oral and patch, plus Night Caps', u: LINKS.restBundle }
  ];

  /* ------------------------------------------------------------------
   * 2. Helpers
   * ------------------------------------------------------------------ */

  var ICONS = {
    mind: '<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3Z"/>',
    wake: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"/><path d="M17 3v4M15 5h4"/>',
    ache: '<path d="M4 18c2-4 5-6 8-6s6 2 8 6"/><path d="M12 12V6"/><path d="M9 4l3 2 3-2"/><circle cx="12" cy="15" r="1.5"/>',
    stress: '<path d="M3 12h3l2-5 3 10 3-7 2 2h5"/>',
    bath: '<path d="M4 12h16v3a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-3Z"/><path d="M6 12V6a2 2 0 0 1 4 0"/><path d="M7 19l-1 2M17 19l1 2"/>'
  };

  function icon(name) {
    if (!ICONS[name]) return '';
    return '<svg class="rf-opt__icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' + ICONS[name] + '</svg>';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function url(path) {
    if (!path) return '#';
    if (/^https?:\/\//.test(path)) return path;
    return root().replace(/\/$/, '') + path;
  }

  function track(event, data) {
    var payload = Object.assign({ event: event }, data || {});
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) { /* noop */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, data || {});
      }
    } catch (e) { /* noop */ }
  }

  function findOption(q, v) {
    for (var i = 0; i < q.options.length; i++) if (q.options[i].v === v) return q.options[i];
    return null;
  }

  function getQuestion(id) {
    for (var i = 0; i < QUESTIONS.length; i++) if (QUESTIONS[i].id === id) return QUESTIONS[i];
    return null;
  }

  /* ------------------------------------------------------------------
   * 3. The finder
   * ------------------------------------------------------------------ */

  function ReliefFinder(el) {
    this.el = el;
    this.app = el.querySelector('[data-rf-app]');
    this.afterAdd = el.getAttribute('data-after-add') || 'cart';
    this.preview = el.hasAttribute('data-rf-preview');
    this.live = {};
    var json = el.querySelector('[data-rf-products]');
    if (json) {
      try { this.live = JSON.parse(json.textContent); } catch (e) { this.live = {}; }
    }
    var missing = Object.keys(PRODUCTS).filter(function (k) { return !this.live[k] || this.live[k].found === false; }, this);
    if (!this.preview && missing.length && window.console) {
      console.warn('[Rest Finder] These products were not found or have no handle set, so they are skipped:', missing.join(', '));
    }
    if (!this.preview && window.Shopify && window.Shopify.designMode && missing.length) {
      var warn = document.createElement('p');
      warn.className = 'rf-admin-note';
      warn.textContent = 'Editor note (shoppers don’t see this): these products weren’t found — check their handles in section settings: ' + missing.join(', ');
      el.querySelector('.rf__inner').insertBefore(warn, this.app);
    }
    this.answers = {};
    this.step = 0;
    this.started = false;

    var preset = el.getAttribute('data-preset-area');
    var params = new URLSearchParams(window.location.search);
    var fromUrl = false;
    var self = this;
    QUESTIONS.forEach(function (q) {
      var v = params.get(q.id);
      if (v && findOption(q, v)) { self.answers[q.id] = v; fromUrl = true; }
    });
    if (!this.answers.area && preset && findOption(QUESTIONS[0], preset)) this.answers.area = preset;
    this.presetArea = this.answers.area || null;

    this.bind();
    if (fromUrl && this.isComplete()) {
      this.renderResult(true);
    } else {
      this.step = this.firstUnanswered();
      this.renderQuestion();
    }
  }

  ReliefFinder.prototype.visible = function () {
    var a = this.answers;
    return QUESTIONS.filter(function (q) { return !q.showIf || q.showIf(a); });
  };

  ReliefFinder.prototype.firstUnanswered = function () {
    var vis = this.visible();
    for (var i = 0; i < vis.length; i++) if (!this.answers[vis[i].id]) return i;
    return vis.length;
  };

  ReliefFinder.prototype.isComplete = function () {
    return this.firstUnanswered() >= this.visible().length;
  };

  ReliefFinder.prototype.productData = function (key) {
    var meta = PRODUCTS[key];
    var live = this.live[key] || {};
    return {
      key: key,
      name: live.title || meta.name,
      url: live.url || null,
      price: live.price || '',
      image: live.image || null,
      variantId: live.variantId || null,
      variantTitle: live.variantTitle || '',
      hasVariants: !!live.hasVariants,
      available: live.available !== false,
      found: live.found !== false,
      meta: meta
    };
  };

  ReliefFinder.prototype.bind = function () {
    var self = this;
    this.el.addEventListener('click', function (e) {
      var t = e.target.closest('[data-rf-action]');
      if (!t || !self.el.contains(t)) return;
      var action = t.getAttribute('data-rf-action');
      if (action === 'answer') self.answer(t.getAttribute('data-q'), t.getAttribute('data-v'));
      else if (action === 'back') self.back();
      else if (action === 'restart') self.restart();
      else if (action === 'edit') self.edit();
      else if (action === 'ingredient') self.toggleIngredient(t);
      else if (action === 'share') self.share(t);
      else if (action === 'change-area') { delete self.answers.area; self.presetArea = null; self.step = 0; self.renderQuestion(); }
    });

    this.el.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-rf-form]');
      if (form && self.el.contains(form)) self.submit(form, e);
    });

    // Open glossary <details> when someone follows an #rf-ing-* link
    function openHash() {
      var id = window.location.hash.slice(1);
      if (!id || id.indexOf('rf-') !== 0) return;
      var target = document.getElementById(id);
      if (target && target.tagName === 'DETAILS') target.open = true;
    }
    window.addEventListener('hashchange', openHash);
    openHash();
  };

  ReliefFinder.prototype.answer = function (qid, v) {
    if (!this.started) { this.started = true; track('rest_finder_start', { preset_area: this.presetArea || '' }); }
    this.answers[qid] = v;
    track('rest_finder_answer', { question: qid, answer: v });

    // Drop answers that no longer apply (e.g. switching to/from the sleep path)
    var vis = this.visible().map(function (q) { return q.id; });
    var self = this;
    Object.keys(this.answers).forEach(function (k) { if (vis.indexOf(k) === -1) delete self.answers[k]; });

    var idx = vis.indexOf(qid);
    this.step = idx + 1;
    if (this.step >= vis.length) this.renderResult();
    else this.renderQuestion();
  };

  ReliefFinder.prototype.back = function () {
    if (this.step > 0) { this.step--; this.renderQuestion(); }
  };

  ReliefFinder.prototype.restart = function () {
    this.answers = {};
    this.presetArea = null;
    this.editing = false;
    this.step = 0;
    this.clearUrl();
    this.renderQuestion();
    track('rest_finder_restart', {});
  };

  ReliefFinder.prototype.edit = function () {
    this.editing = false;
    this.step = 0;
    this.renderQuestion();
  };

  ReliefFinder.prototype.focusApp = function () {
    var h = this.app.querySelector('[data-rf-focus]');
    if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
    var rect = this.app.getBoundingClientRect();
    if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
      this.app.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /* ---------- Question screen ---------- */

  ReliefFinder.prototype.renderQuestion = function () {
    var vis = this.visible();
    var q = vis[this.step];
    if (!q) { this.renderResult(); return; }
    var total = vis.length;
    var pct = Math.round((this.step / total) * 100);
    var current = this.answers[q.id];
    var self = this;

    var presetNote = '';
    if (this.presetArea && q.id !== 'area' && this.answers.area === this.presetArea) {
      var areaOpt = findOption(QUESTIONS[0], this.presetArea);
      presetNote = '<p class="rf-preset">Building a routine for <strong>' + esc(areaOpt.summary) +
        '</strong> · <button type="button" class="rf-link" data-rf-action="change-area">Change</button></p>';
    }

    var opts = q.options.map(function (o) {
      var pressed = current === o.v;
      return '<button type="button" class="rf-opt' + (o.icon ? ' rf-opt--icon' : '') + (pressed ? ' is-selected' : '') +
        '" data-rf-action="answer" data-q="' + q.id + '" data-v="' + o.v + '" aria-pressed="' + pressed + '">' +
        icon(o.icon) + '<span class="rf-opt__label">' + esc(o.label) + '</span></button>';
    }).join('');

    this.app.innerHTML =
      '<div class="rf-card rf-card--question">' +
        '<div class="rf-progress" role="progressbar" aria-valuemin="0" aria-valuemax="' + total + '" aria-valuenow="' + this.step + '" aria-label="Question ' + (this.step + 1) + ' of ' + total + '">' +
          '<span style="width:' + pct + '%"></span></div>' +
        '<p class="rf-step">Step ' + (this.step + 1) + ' of ' + total + '</p>' +
        presetNote +
        '<fieldset class="rf-q"><legend class="rf-q__title" data-rf-focus>' + esc(q.title) + '</legend>' +
        (q.help ? '<p class="rf-q__help">' + esc(q.help) + '</p>' : '') +
        '<div class="rf-opts' + (q.id === 'area' ? ' rf-opts--grid' : '') + '">' + opts + '</div></fieldset>' +
        '<div class="rf-nav">' +
          (this.step > 0 ? '<button type="button" class="rf-link" data-rf-action="back">← Back</button>' : '<span></span>') +
          (Object.keys(this.answers).length ? '<button type="button" class="rf-link" data-rf-action="restart">Start over</button>' : '') +
        '</div>' +
      '</div>';

    if (this.started) this.focusApp();
    void self;
  };

  /* ---------- Scoring ---------- */

  ReliefFinder.prototype.compute = function () {
    var a = this.answers;
    var self = this;
    var totals = {};
    var contrib = {};
    Object.keys(PRODUCTS).forEach(function (k) { totals[k] = 0; contrib[k] = []; });

    this.visible().forEach(function (q) {
      var v = a[q.id];
      var map = (SCORES[q.id] || {})[v] || {};
      Object.keys(map).forEach(function (k) {
        totals[k] += map[k];
        if (map[k] > 0) contrib[k].push([q.id, v]);
      });
    });

    var eligible = Object.keys(PRODUCTS).filter(function (k) {
      var m = PRODUCTS[k];
      if (m.sample) return false;
      if (m.onlyIf && !m.onlyIf(a)) return false;
      if (a.exp === 'nocbd' && m.cbd) return false;
      var d = self.productData(k);
      return d.found && d.available;
    });

    eligible.sort(function (x, y) {
      if (totals[y] !== totals[x]) return totals[y] - totals[x];
      var px = PRIORITY.indexOf(x), py = PRIORITY.indexOf(y);
      return (px === -1 ? 99 : px) - (py === -1 ? 99 : py);
    });

    var primary = eligible[0];
    var pFormat = PRODUCTS[primary].format;
    var pair = eligible.filter(function (k) { return PRODUCTS[k].format !== pFormat && totals[k] > 0; })[0] || null;
    var alt = eligible.filter(function (k) { return k !== primary && k !== pair && PRODUCTS[k].format === pFormat && totals[k] > 0; })[0] || null;

    var reasons = [];
    contrib[primary].forEach(function (pair) {
      var txt = (REASONS[pair[0]] || {})[pair[1]];
      if (txt && reasons.indexOf(txt) === -1) reasons.push(txt);
    });

    return { primary: primary, pair: pair, alt: alt, totals: totals, reasons: reasons.slice(0, 3) };
  };

  /* ---------- Result screen ---------- */

  ReliefFinder.prototype.buyForm = function (key, d, label, cls) {
    // A real Shopify cart form: works with every theme even if JavaScript add-to-cart is blocked.
    return '<form class="rf-buy" method="post" action="' + esc(url('/cart/add')) + '" accept-charset="UTF-8" data-rf-form data-key="' + key + '">' +
      '<input type="hidden" name="form_type" value="product">' +
      '<input type="hidden" name="utf8" value="✓">' +
      '<input type="hidden" name="id" value="' + esc(d.variantId || '') + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<input type="hidden" name="properties[_source]" value="Rest Finder">' +
      '<input type="hidden" name="return_to" value="' + esc(url('/cart')) + '">' +
      '<button type="submit" class="rf-btn ' + cls + '">' + label + '</button></form>';
  };

  ReliefFinder.prototype.productCard = function (key, variant) {
    var d = this.productData(key);
    var m = d.meta;
    var self = this;
    var isMain = variant === 'main';
    var img = d.image
      ? '<img src="' + esc(d.image) + '" alt="' + esc(d.name) + '" loading="lazy" width="240" height="240" onerror="this.parentNode.classList.add(\'is-empty\');this.remove()">'
      : '';
    var chips = (m.ingredients || []).map(function (id) {
      return '<button type="button" class="rf-chip" data-rf-action="ingredient" data-ing="' + id + '" data-card="' + key + '" aria-expanded="false">' +
        esc(INGREDIENT_NAMES[id] || id) + '</button>';
    }).join('');

    var buy = '';
    if (d.variantId || this.preview) {
      buy = this.buyForm(key, d, 'Add to cart' + (d.price ? ' · ' + esc(d.price) : ''), 'rf-btn--primary');
    }
    var details = d.url ? '<a class="rf-btn rf-btn--ghost" href="' + esc(url(d.url)) + '">' + (d.hasVariants ? 'See all sizes' : 'View details') + '</a>' : '';
    var sizeNote = d.hasVariants && d.variantTitle ? '<p class="rf-pcard__size">Adds ' + esc(d.variantTitle) + '</p>' : '';

    void self;
    return '<article class="rf-pcard rf-pcard--' + variant + '">' +
      '<div class="rf-pcard__media' + (img ? '' : ' is-empty') + '">' + img + '</div>' +
      '<div class="rf-pcard__body">' +
        '<p class="rf-badge rf-badge--' + m.format + '">' + esc(FORMATS[m.format].name) + ' · ' + esc(m.type) + '</p>' +
        '<h3 class="rf-pcard__title">' + esc(d.name) + '</h3>' +
        (m.tagline ? '<p class="rf-pcard__tag">' + esc(m.tagline) + '</p>' : '') +
        (isMain ? '<dl class="rf-facts"><div><dt>Feel it</dt><dd>' + esc(m.onset) + '</dd></div><div><dt>Lasts</dt><dd>' + esc(m.duration) + '</dd></div><div><dt>Strength</dt><dd>' + esc(m.detail) + '</dd></div></dl>' : '') +
        (chips ? '<div class="rf-ings"><p class="rf-ings__label">What’s inside <span>— tap to learn</span></p><div class="rf-chips">' + chips + '</div><div class="rf-ing-panel" data-panel="' + key + '" hidden></div></div>' : '') +
        '<div class="rf-pcard__actions">' + buy + details + '</div>' + sizeNote +
        '<p class="rf-added" data-added="' + key + '" hidden></p>' +
      '</div></article>';
  };

  ReliefFinder.prototype.renderResult = function (restored) {
    var r = this.compute();
    var a = this.answers;
    var pMeta = PRODUCTS[r.primary];
    var fmt = FORMATS[pMeta.format];
    var areaOpt = findOption(getQuestion('area'), a.area);
    var secondQ = a.area === 'sleep' ? getQuestion('sleepwhy') : getQuestion('pattern');
    var secondOpt = findOption(secondQ, a[secondQ.id]);
    var pName = this.productData(r.primary).name;

    var sentence = 'For ' + esc(areaOpt.summary) + (secondOpt ? ' ' + esc(secondOpt.summary) : '') +
      ', we’d start with <strong>' + esc(pMeta.phrase || (fmt.article + ' ' + fmt.long)) + '</strong>.';

    var reasons = r.reasons.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');

    // Format comparison strip
    var strip = Object.keys(FORMATS).map(function (k) {
      var f = FORMATS[k];
      var on = k === pMeta.format;
      return '<div class="rf-fmt' + (on ? ' is-match' : '') + '">' +
        (on ? '<span class="rf-fmt__flag">Your match</span>' : '') +
        '<h4>' + esc(f.name) + '</h4>' +
        '<dl><div><dt>Feel it</dt><dd>' + esc(f.onset) + '</dd></div><div><dt>Lasts</dt><dd>' + esc(f.duration) + '</dd></div><div><dt>Works</dt><dd>' + esc(f.reach) + '</dd></div></dl>' +
        '<p>' + esc(f.blurb) + '</p></div>';
    }).join('');

    // Pair
    var pairHtml = '';
    if (r.pair) {
      var pairName = this.productData(r.pair).name;
      var key = pMeta.format + '>' + PRODUCTS[r.pair].format;
      var txt = (PAIR_TEXT[key] || 'Pair it with the {pair}.').replace('{primary}', pName).replace('{pair}', pairName);
      pairHtml = '<section class="rf-block"><h3 class="rf-block__title">Complete your routine</h3>' +
        '<p class="rf-block__lead">' + esc(txt) + '</p>' + this.productCard(r.pair, 'pair') +
        '<p class="rf-save">Mix &amp; match: <a href="' + esc(url(LINKS.bundle3)) + '">buy any 3 products, save 15%</a> · <a href="' + esc(url(LINKS.subscribe)) + '">subscribe &amp; save 15%</a></p></section>';
    }

    if (!r.pair && a.exp === 'nocbd') {
      pairHtml = '<p class="rf-tip">Night Caps are our only CBD-free sleep product. If you change your mind about CBD, our <a href="' + esc(url(LINKS.relaxLine)) + '">Relax line</a> pairs calming essential oils with THC-free CBD.</p>';
    }
    var altHtml = '';
    if (r.alt) {
      var ad = this.productData(r.alt);
      altHtml = '<p class="rf-alt">Prefer a different feel? Try <a href="' + esc(url(ad.url || '#')) + '">' + esc(ad.name) + '</a> — ' + esc(PRODUCTS[r.alt].tagline || '') + '</p>';
    }

    // New to CBD → sampler
    var sampleHtml = '';
    if (a.exp === 'new') {
      var sKey = 'sample_oral';
      var sd = this.productData(sKey);
      if (sd.found) {
        sampleHtml = '<aside class="rf-sample"><div><h3>New to CBD? Try before you commit.</h3>' +
          '<p>Our ' + esc(sd.name) + (sd.price ? ' (' + esc(sd.price) + ')' : '') + ' lets you test a few formats and find your favorite.</p></div>' +
          '<div class="rf-sample__actions">' +
          (sd.variantId || this.preview ? this.buyForm(sKey, sd, 'Add sampler', 'rf-btn--secondary') : '') +
          '<a class="rf-link" href="' + esc(url(LINKS.samples)) + '">See all samplers</a></div>' +
          '<p class="rf-added" data-added="' + sKey + '" hidden></p></aside>';
      }
    }

    // Learn more
    var seen = {};
    var articles = [].concat(AREA_ARTICLES[a.area] || [], FORMAT_ARTICLES[pMeta.format] || [], [{ t: 'Lab results (COAs) for every batch', u: LINKS.coa }])
      .filter(function (x) { if (seen[x.u]) return false; seen[x.u] = 1; return true; });
    var learn = '<section class="rf-block rf-learnmore"><h3 class="rf-block__title">Want to learn more?</h3><ul>' +
      articles.map(function (x) { return '<li><a href="' + esc(url(x.u)) + '">' + esc(x.t) + ' →</a></li>'; }).join('') +
      (document.getElementById('rf-learn') ? '<li><a href="#rf-learn">Compare every format &amp; ingredient on this page ↓</a></li>' : '') +
      '</ul></section>';

    this.app.innerHTML =
      '<div class="rf-card rf-card--result">' +
        '<p class="rf-step">Your rest routine</p>' +
        '<h2 class="rf-result__title" data-rf-focus>' + sentence + '</h2>' +
        this.productCard(r.primary, 'main') +
        (reasons ? '<section class="rf-block"><h3 class="rf-block__title">Why it fits you</h3><ul class="rf-reasons">' + reasons + '</ul></section>' : '') +
        (AREA_TIPS[a.area] ? '<p class="rf-tip">' + esc(AREA_TIPS[a.area]) + '</p>' : '') +
        altHtml +
        sampleHtml +
        pairHtml +
        '<section class="rf-block rf-habits"><h3 class="rf-block__title">Pair it with habits that help</h3><ul class="rf-reasons">' +
          HABITS.map(function (h) { return '<li>' + esc(h) + '</li>'; }).join('') + '</ul></section>' +
        (a.exp !== 'nocbd' ? '<section class="rf-block"><h3 class="rf-block__title">Save on a complete routine</h3><ul class="rf-bundles">' +
          BUNDLES.map(function (x) { return '<li><a href="' + esc(url(x.u)) + '">' + esc(x.t) + '</a> — ' + esc(x.d) + '</li>'; }).join('') + '</ul></section>' : '') +
        '<section class="rf-block"><h3 class="rf-block__title">How the formats compare</h3><div class="rf-fmts">' + strip + '</div></section>' +
        learn +
        '<div class="rf-nav rf-nav--result">' +
          '<button type="button" class="rf-link" data-rf-action="edit">← Change my answers</button>' +
          '<button type="button" class="rf-link" data-rf-action="share">Copy link to my results</button>' +
          '<button type="button" class="rf-link" data-rf-action="restart">Start over</button>' +
        '</div>' +
      '</div>';

    this.writeUrl();
    track('rest_finder_complete', {
      answers: Object.assign({}, a),
      primary: r.primary, primary_format: pMeta.format,
      pair: r.pair || '', restored: !!restored
    });
    this.result = r;
    if (!restored) this.focusApp();
  };

  /* ---------- Ingredients ---------- */

  ReliefFinder.prototype.toggleIngredient = function (btn) {
    var id = btn.getAttribute('data-ing');
    var card = btn.getAttribute('data-card');
    var panel = this.app.querySelector('[data-panel="' + card + '"]');
    if (!panel) return;
    var wasOpen = btn.getAttribute('aria-expanded') === 'true';
    this.app.querySelectorAll('.rf-chip[data-card="' + card + '"]').forEach(function (c) {
      c.setAttribute('aria-expanded', 'false'); c.classList.remove('is-active');
    });
    if (wasOpen) { panel.hidden = true; return; }

    var src = document.getElementById('rf-ing-' + id);
    var body = src ? src.querySelector('.rf-gloss__body') : null;
    panel.innerHTML = '<h4>' + esc(INGREDIENT_NAMES[id] || id) + '</h4>' +
      (body ? body.innerHTML : '<p>More details coming soon.</p>') +
      (src ? '<p><a href="#rf-ing-' + id + '">See the full ingredient guide ↓</a></p>' : '');
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    btn.classList.add('is-active');
    track('rest_finder_ingredient', { ingredient: id, product: card });
  };

  /* ---------- Cart ---------- */

  ReliefFinder.prototype.submit = function (form, e) {
    var key = form.getAttribute('data-key');
    var variant = form.querySelector('input[name="id"]').value;
    var btn = form.querySelector('button');
    var note = this.app.querySelector('[data-added="' + key + '"]');
    var label = btn.innerHTML;
    var self = this;
    var d = this.productData(key);
    var info = { product: key, variant_id: variant, price: d.price, primary: this.result && this.result.primary };

    // Preview file: nothing to add to
    if (this.preview || !variant) {
      e.preventDefault();
      btn.innerHTML = 'Added ✓';
      if (note) { note.hidden = false; note.textContent = 'Preview mode — on your store this adds the product and opens the cart.'; }
      return;
    }

    track('rest_finder_add_to_cart', info);

    // "Go to cart": let the browser submit the form. Shopify adds the item and
    // redirects to the cart itself — the most reliable path on any theme.
    if (this.afterAdd !== 'stay') {
      btn.innerHTML = 'Adding…';
      return;
    }

    // "Stay on page": add in the background; if that fails, fall back to the form.
    e.preventDefault();
    btn.disabled = true;
    btn.innerHTML = 'Adding…';
    fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1, properties: { _source: 'Rest Finder' } }] })
    }).then(function (res) {
      return res.json().then(function (j) {
        if (!res.ok) throw new Error(j.description || j.message || 'Could not add to cart');
        return j;
      });
    }).then(function () {
      btn.disabled = false;
      btn.innerHTML = 'Added ✓';
      btn.classList.add('is-added');
      if (note) { note.hidden = false; note.innerHTML = 'Added to your cart. <a href="' + esc(url('/cart')) + '">View cart →</a>'; }
      document.dispatchEvent(new CustomEvent('rest-finder:added', { detail: { key: key, variantId: variant } }));
      ['cart:refresh', 'cart:updated', 'theme:cart:refresh'].forEach(function (n) {
        document.dispatchEvent(new CustomEvent(n, { bubbles: true }));
      });
      self.refreshCartCount();
    }).catch(function (err) {
      btn.disabled = false;
      btn.innerHTML = label;
      if (window.console) console.warn('[Rest Finder] background add failed, submitting form instead:', err);
      if (err && /sold out|not available|already sold/i.test(err.message)) {
        if (note) { note.hidden = false; note.textContent = err.message; }
        return;
      }
      HTMLFormElement.prototype.submit.call(form);
    });
  };

  ReliefFinder.prototype.refreshCartCount = function () {
    fetch(url('/cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count, [data-header-cart-count]').forEach(function (n) {
          n.textContent = cart.item_count;
          n.hidden = false;
        });
      }).catch(function () {});
  };

  /* ---------- URL state ---------- */

  ReliefFinder.prototype.writeUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    QUESTIONS.forEach(function (q) { params.delete(q.id); });
    var a = this.answers;
    this.visible().forEach(function (q) { if (a[q.id]) params.set(q.id, a[q.id]); });
    var qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
  };

  ReliefFinder.prototype.clearUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    QUESTIONS.forEach(function (q) { params.delete(q.id); });
    var qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  };

  ReliefFinder.prototype.share = function (btn) {
    var link = window.location.href.split('#')[0];
    var orig = btn.textContent;
    function ok() { btn.textContent = 'Link copied ✓'; setTimeout(function () { btn.textContent = orig; }, 2000); }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(ok, function () { window.prompt('Copy this link:', link); });
    } else {
      window.prompt('Copy this link:', link);
    }
    track('rest_finder_share', {});
  };

  /* ------------------------------------------------------------------
   * 4. Boot (also re-inits inside the theme editor)
   * ------------------------------------------------------------------ */

  function init(scope) {
    (scope || document).querySelectorAll('[data-rest-finder]').forEach(function (el) {
      if (el.__restf) return;
      el.__restf = new ReliefFinder(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.BasicJaneRestFinder = { init: init, config: { PRODUCTS: PRODUCTS, QUESTIONS: QUESTIONS, SCORES: SCORES, LINKS: LINKS } };
})();